// SPDX-License-Identifier: Apache-2.0
// File: src/ui/theme/PocketGridRetroTheme.ts

export const PocketGridRetroPalette = {
  chassisGunmetal: '#1E1F22',
  brushedTitanium: '#282A2E',
  panelBorder: '#383A40',
  recessedTrack: '#141517',
  warmWalnut: '#4A3222',
  warmIvoryText: '#ECE5D5',
  mutedPewterText: '#9A958C',
  brushedMetalCap: '#D5CFC4',

  // Refined Retro-Modern Illumination Accents (Zero Poppy Neon)
  tungstenAmber: '#D9943B',
  incandescentCream: '#FFF3D6',
  vintageVfdSage: '#5E9E8B',
  burntTerracotta: '#C9643B',
  champagneBrass: '#C4A46B',
  studioTapeRed: '#C83E37',
  phosphorMoss: '#4E9A67',
  electricTeal: '#2DE2C4',
} as const;

export type SynthParamId =
  | 'algMorph'
  | 'waveFold'
  | 'fmFeedback'
  | 'resonatorDecay'
  | 'formantShift'
  | 'vowelMorphX'
  | 'chorusDelayMix'
  | 'spaceReverbMix'
  | 'modAttack'
  | 'modDecay'
  | 'carAttack'
  | 'carDecay'
  | 'vowelMorphY'
  | 'sibilance'
  | 'drumDispersion'
  | 'wowFlutter'
  | 'limiterDrive'
  | 'seqSwing';

export interface SliderParamMeta {
  id: SynthParamId;
  label: string;
  category: 'FM Synth' | 'Drums' | 'Vocoder' | 'Master FX' | 'FM Envelopes' | 'Sequencer';
  defaultValue: number;
  formatDisplay: (v: number) => string;
}

export const ALL_ASSIGNABLE_PARAMS: Record<SynthParamId, SliderParamMeta> = {
  algMorph: {
    id: 'algMorph',
    label: 'ALG MORPH',
    category: 'FM Synth',
    defaultValue: 0.35,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  waveFold: {
    id: 'waveFold',
    label: 'WAVE FOLD',
    category: 'FM Synth',
    defaultValue: 0.64,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  fmFeedback: {
    id: 'fmFeedback',
    label: 'FM FEEDBACK',
    category: 'FM Synth',
    defaultValue: 0.48,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  resonatorDecay: {
    id: 'resonatorDecay',
    label: 'RESONATOR DECAY',
    category: 'Drums',
    defaultValue: 0.75,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  formantShift: {
    id: 'formantShift',
    label: 'FORMANT SHIFT',
    category: 'Vocoder',
    defaultValue: 0.58,
    formatDisplay: (v) => {
      const st = Math.round((v - 0.5) * 24);
      return `${st >= 0 ? '+' : ''}${st} st`;
    },
  },
  vowelMorphX: {
    id: 'vowelMorphX',
    label: 'VOWEL MORPH X',
    category: 'Vocoder',
    defaultValue: 0.42,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  chorusDelayMix: {
    id: 'chorusDelayMix',
    label: 'CHORUS + DELAY',
    category: 'Master FX',
    defaultValue: 0.30,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  spaceReverbMix: {
    id: 'spaceReverbMix',
    label: 'SPACE REVERB',
    category: 'Master FX',
    defaultValue: 0.24,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  modAttack: {
    id: 'modAttack',
    label: 'MOD ATT',
    category: 'FM Envelopes',
    defaultValue: 0.24,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  modDecay: {
    id: 'modDecay',
    label: 'MOD DEC',
    category: 'FM Envelopes',
    defaultValue: 0.68,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  carAttack: {
    id: 'carAttack',
    label: 'CAR ATT',
    category: 'FM Envelopes',
    defaultValue: 0.12,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  carDecay: {
    id: 'carDecay',
    label: 'CAR DEC',
    category: 'FM Envelopes',
    defaultValue: 0.52,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  vowelMorphY: {
    id: 'vowelMorphY',
    label: 'VOWEL MORPH Y',
    category: 'Vocoder',
    defaultValue: 0.5,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  sibilance: {
    id: 'sibilance',
    label: 'VOC SIBILANCE',
    category: 'Vocoder',
    defaultValue: 0.22,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  drumDispersion: {
    id: 'drumDispersion',
    label: 'MEMBRANE DISP',
    category: 'Drums',
    defaultValue: 0.4,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  wowFlutter: {
    id: 'wowFlutter',
    label: 'WOW & FLUTTER',
    category: 'Master FX',
    defaultValue: 0.18,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  limiterDrive: {
    id: 'limiterDrive',
    label: 'TAPE DRIVE',
    category: 'Master FX',
    defaultValue: 0.25,
    formatDisplay: (v) => `${Math.round(v * 100)}%`,
  },
  seqSwing: {
    id: 'seqSwing',
    label: 'SEQ SWING',
    category: 'Sequencer',
    defaultValue: 0.54,
    formatDisplay: (v) => `${Math.round(50 + v * 25)}%`,
  },
};

export const STUDIO_DEFAULT_SLIDER_BANKS: {
  leftBay: SynthParamId[];
  centerBay: SynthParamId[];
  rightBay: SynthParamId[];
} = {
  leftBay: ['algMorph', 'waveFold', 'fmFeedback', 'resonatorDecay'],
  centerBay: ['formantShift', 'vowelMorphX', 'chorusDelayMix', 'spaceReverbMix'],
  rightBay: ['modAttack', 'modDecay', 'carAttack', 'carDecay'],
};
